import { FormEvent, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { requestPasswordReset, ResetRequestResp } from "../../lib/api";

/** Admin tool: issue a password-reset link for a user you outrank in
 * your tenant (the backend authorizes). Shows the real email status
 * plus a copy-able reset link, so you can get someone back in even
 * when email delivery is down. */
export function ResetPasswordForm() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<ResetRequestResp | null>(null);

  const mut = useMutation({
    mutationFn: () => requestPasswordReset(email.trim()),
    onSuccess: (resp) => {
      setEmail("");
      setResult(resp);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || mut.isPending) return;
    setResult(null);
    mut.mutate();
  };

  const link = result
    ? `${window.location.origin}/reset/${result.reset_token}`
    : null;

  return (
    <div className="bg-white rounded shadow p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-1">
        Reset a user's password
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        Sends a reset link to a user in your scope. You'll also get a
        copy-able link here as a fallback if email is down.
      </p>
      <form onSubmit={onSubmit} className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px]">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-agrilens-green"
          />
        </div>
        <button
          type="submit"
          disabled={mut.isPending}
          className="bg-agrilens-green text-white text-sm font-medium px-4 py-2 rounded hover:bg-agrilens-green-dark disabled:opacity-50"
        >
          {mut.isPending ? "Sending…" : "Send reset link"}
        </button>
      </form>

      {mut.isError && (
        <div className="mt-2 text-xs text-red-700 bg-red-50 rounded p-2">
          {(mut.error as Error).message}
        </div>
      )}
      {result && (
        <div className="mt-2 space-y-2">
          {result.email_sent ? (
            <div className="text-xs text-green-700 bg-green-50 rounded p-2">
              Reset link emailed to{" "}
              <span className="font-medium">{result.email}</span>.
            </div>
          ) : (
            <div className="text-xs text-amber-800 bg-amber-50 rounded p-2">
              Created, but the email couldn't be sent. Share the link
              below directly.
            </div>
          )}
          {link && <CopyLink link={link} />}
        </div>
      )}
    </div>
  );
}

function CopyLink({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.getElementById(
        `reset-link-${btoa(link).slice(0, 8)}`,
      ) as HTMLInputElement | null;
      el?.select();
    }
  };
  return (
    <div className="flex items-stretch gap-2">
      <input
        id={`reset-link-${btoa(link).slice(0, 8)}`}
        readOnly
        value={link}
        onFocus={(e) => e.currentTarget.select()}
        className="flex-1 min-w-0 border rounded px-2 py-1 text-xs font-mono bg-gray-50"
      />
      <button
        type="button"
        onClick={copy}
        className={`shrink-0 text-xs font-medium px-3 py-1 rounded ${
          copied
            ? "bg-green-600 text-white"
            : "bg-agrilens-green text-white hover:bg-agrilens-green-dark"
        }`}
      >
        {copied ? "Copied!" : "Copy link"}
      </button>
    </div>
  );
}
